import React from "react";
import PropTypes from "prop-types";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import DraggableImage from "../DraggableImage/DraggableImage";

const isSupabaseUrl = (url) => url && url.includes("supabase.co");

const sortByCreationTime = (a, b) => {
  const aTime = new Date(
    a.mediaMetadata?.creationTime || a.creationTime || 0
  ).getTime();
  const bTime = new Date(
    b.mediaMetadata?.creationTime || b.creationTime || 0
  ).getTime();
  return bTime - aTime;
};

const PhotoStagingArea = ({ id, photos, albumName, uploadProgress, isUploading, onRemovePhoto }) => {
  const sortedPhotos = [...photos].sort(sortByCreationTime);

  return (
    <div className="mt-8">
      <div className="mb-3">
        <h3 className="text-xl font-semibold">
          Photos from: <span className="font-normal">{albumName}</span>
        </h3>
        {isUploading && uploadProgress.total > 0 && (
          <div className="mt-2">
            <div className="flex items-center justify-between text-sm text-zinc-400 mb-1">
              <span>Upload Progress</span>
              <span>{uploadProgress.current} / {uploadProgress.total}</span>
            </div>
            <div className="w-full bg-zinc-700 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>
      <SortableContext
        items={sortedPhotos.map((p) => p.photoId)}
        strategy={rectSortingStrategy}
      >
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4 p-4 border border-zinc-700 rounded-lg bg-zinc-900">
          {sortedPhotos.map((photo) => {
            let thumbSrc = "";
            let fullSrc = photo.baseUrl || "";

            if (isSupabaseUrl(photo.baseUrl)) {
              thumbSrc = `${photo.baseUrl}?width=256&height=256`;
              fullSrc = photo.baseUrl;
            } else if (photo.baseUrl) {
              thumbSrc = `/api/google/photos/proxy?url=${encodeURIComponent(
                photo.baseUrl
              )}&size=w256-h256-c`;
            }

            const imageWithThumb = { ...photo, thumbSrc };

            return (
              <div key={photo.photoId} className="w-24 h-28 text-center">
                <DraggableImage
                  id={photo.photoId}
                  image={imageWithThumb}
                  containerId={id}
                  onRemove={onRemovePhoto ? () => onRemovePhoto(photo.photoId) : null}
                  onPreviewClick={() => window.open(fullSrc, "_blank")}
                />
              </div>
            );
          })}
          {photos.length === 0 && (
            <p className="text-zinc-500 col-span-full text-center py-8">
              Select photos to begin.
            </p>
          )}
        </div>
      </SortableContext>
    </div>
  );
};

PhotoStagingArea.propTypes = {
  id: PropTypes.string.isRequired,
  photos: PropTypes.arrayOf(PropTypes.object).isRequired,
  albumName: PropTypes.string,
  uploadProgress: PropTypes.shape({
    current: PropTypes.number,
    total: PropTypes.number,
  }),
  isUploading: PropTypes.bool,
  onRemovePhoto: PropTypes.func,
};

export default PhotoStagingArea;
